/**
 * PartnerDashboardHome — iter98
 * Summary dashboard. All detailed actions live in the sidebar.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Store,
  TrendingUp,
  Clock,
  CalendarRange,
  Wallet,
  Banknote,
  Loader2,
  AlertCircle,
  ArrowRight,
} from 'lucide-react';
import PartnerAccountLayout from '../../../components/PartnerAccountLayout';
import PartnerNextBestAction from '../../../components/PartnerNextBestAction';
import { StripeWordmark } from '../../../components/PaymentLogos';
import { API, fmtMoney, authHeaders } from './_utils';

function StatCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${
        accent
          ? 'bg-gradient-to-br from-amber-500/[0.08] to-gold-500/[0.04] border-gold-500/30'
          : 'bg-white/[0.025] border-white/[0.08]'
      }`}
      data-testid={testId}
    >
      <Icon className={`w-5 h-5 mb-3 ${accent ? 'text-gold-300' : 'text-white/55'}`} />
      <div className={`text-3xl font-black ${accent ? 'text-gold-200' : 'text-white'}`}>{value}</div>
      <p className="text-xs uppercase tracking-[0.18em] text-white/45 mt-2 font-semibold">{label}</p>
      {sub && <p className="text-xs text-white/40 mt-1">{sub}</p>}
    </div>
  );
}

export default function PartnerDashboardHome() {
  const [partner, setPartner] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  // iter106 — One-time welcome toast after registration.
  // The onboarding page now redirects here with `?welcome=1` so we can
  // greet the partner inside the dashboard (where the Stripe-Connect
  // banner already lives) instead of dropping them on a banking CTA.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('welcome') !== '1') return;
    toast.success('Welcome to NoFee Partners!', {
      description: 'Connect Stripe from the banner above to start receiving your 10% recurring revenue.',
      duration: 7500,
    });
    // Strip the flag so a refresh doesn't re-fire the toast.
    window.history.replaceState({}, '', '/partners/dashboard');
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/partners/dashboard`, { headers: authHeaders() });
        setPartner(r.data.partner);
        setOverview(r.data.overview);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem('nofee_partner_token');
          window.location.href = '/partners/login';
        } else {
          toast.error('Could not load dashboard');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <PartnerAccountLayout currentKey="dashboard" title="Dashboard">
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-gold-300" /></div>
      </PartnerAccountLayout>
    );
  }
  if (!partner) return null;

  const stripeConnected = !!partner.stripe_details_submitted;

  return (
    <PartnerAccountLayout
      currentKey="dashboard"
      title="Dashboard"
      subtitle={`Welcome back, ${partner.contact_name || partner.organization_name}. Here is a snapshot of your partnership.`}
    >
      {/* Stripe banner if not connected */}
      {!stripeConnected && (
        <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/[0.05] p-5 flex flex-wrap items-center justify-between gap-3" data-testid="partner-dashboard-stripe-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-white">Connect Stripe to receive your recurring revenue</p>
              <p className="text-xs text-white/55 mt-0.5">NoFee never stores banking info — all payouts run through Stripe Connect.</p>
            </div>
          </div>
          <Link
            to="/partners/account/stripe"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-[#635BFF] hover:bg-[#544ee6] text-white font-bold text-xs whitespace-nowrap transition-colors shadow-[0_8px_24px_-8px_rgba(99,91,255,0.55)]"
            data-testid="partner-dashboard-stripe-cta"
          >
            Connect <StripeWordmark className="h-3.5 w-auto text-white" monochrome /> <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatCard icon={Store}         label="Restaurants Joined"        value={overview?.total_referred ?? 0}              testId="partner-stat-joined" />
        <StatCard icon={TrendingUp}    label="Active Restaurants"        value={overview?.active_restaurants ?? 0}          testId="partner-stat-active" />
        <StatCard icon={Clock}         label="Restaurants In Trial"      value={overview?.trial_restaurants ?? 0}           testId="partner-stat-trial" />
        <StatCard icon={CalendarRange} label="This Month Revenue"        value={fmtMoney(overview?.commission_this_month_cents)} accent testId="partner-stat-month" />
        <StatCard icon={Wallet}        label="Total Recurring Revenue"   value={fmtMoney(overview?.commission_total_cents)} sub="Lifetime, paid"   testId="partner-stat-total" />
        <StatCard icon={Banknote}      label="Upcoming Stripe Payout"    value={fmtMoney(overview?.commission_this_month_cents)} sub={stripeConnected ? 'Auto-paid via Stripe' : 'Connect Stripe to enable'} testId="partner-stat-upcoming" />
      </div>

      {/* Next Best Action — dynamic guidance card */}
      <PartnerNextBestAction partner={partner} overview={overview} />
    </PartnerAccountLayout>
  );
}
